import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { useBranding } from "../../context/BrandingContext";
import portalApi from "../../api/portalClient";

type Step = "credentials" | "2fa";

interface PortalSsoProvider {
  id: number;
  name: string;
  protocol: string;
  customer_id: number | null;
  customer_name: string | null;
  login_url: string;
}

export default function PortalLogin() {
  const { login, verifyTwoFactor, acceptSsoToken, user } = usePortalAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [partialToken, setPartialToken] = useState("");
  const [usingBackup, setUsingBackup] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [providers, setProviders] = useState<PortalSsoProvider[]>([]);
  // On-prem AD (relayed through the on-site agent) is a direct credential flow:
  // clicking it reveals an inline form instead of redirecting.
  const [adProvider, setAdProvider] = useState<PortalSsoProvider | null>(null);
  const [adUsername, setAdUsername] = useState("");
  const [adPassword, setAdPassword] = useState("");
  const [adDomain, setAdDomain] = useState("");

  useEffect(() => {
    if (user) navigate("/portal");
  }, [user, navigate]);

  // Enabled customer-audience providers for the "Sign in with X" buttons. If
  // the fetch fails we just hide them; password login still works.
  useEffect(() => {
    portalApi
      .get("/api/v1/portal/sso/providers")
      .then((r) => setProviders(r.data || []))
      .catch(() => setProviders([]));
  }, []);

  const startSso = (p: PortalSsoProvider) => {
    setError("");
    if (p.protocol === "ad") {
      setAdProvider(p);
      setAdUsername("");
      setAdPassword("");
      setAdDomain("");
      return;
    }
    // OIDC/SAML: top-level navigation to the portal init endpoint (must not be
    // an XHR — the IdP round-trip needs a real browser redirect + cookie).
    window.location.href = `${p.login_url}?return_to=${encodeURIComponent("/portal")}`;
  };

  // Relayed on-prem AD sign-in: POST the credentials, then poll the command
  // until the on-site agent reports the bind result (see Phase 3 backend).
  const submitAdLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adProvider) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await portalApi.post("/api/v1/portal/sso/ldap/login", {
        provider_id: adProvider.id,
        username: adUsername.trim(),
        password: adPassword,
        domain: adDomain.trim() || null,
      });
      const commandId = data.command_id;
      // Poll for up to ~60s (agent poll cadence). 2s interval.
      for (let i = 0; i < 30; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        const s = await portalApi.get(`/api/v1/portal/sso/ldap/login/${commandId}`);
        if (s.data.status === "completed" && s.data.token) {
          await acceptSsoToken(s.data.token);
          navigate("/portal");
          return;
        }
        if (s.data.status === "failed") {
          setError(s.data.error || "Active Directory rejected those credentials.");
          return;
        }
        // else still pending → keep polling
      }
      setError(
        "Timed out waiting for the on-site agent to verify your credentials. Try again, or contact support if it persists.",
      );
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Sign in failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.ok) {
        navigate("/portal");
        return;
      }
      if (result.requires2FA) {
        setPartialToken(result.partialToken);
        setStep("2fa");
        setCode("");
        return;
      }
      setError(result.error);
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyTwoFactor(partialToken, code.trim());
      navigate("/portal");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="h-1.5 w-full bg-brand" />

        <div className="p-8">
          <div className="text-center mb-7">
            {branding.has_logo && branding.logo_url && !logoFailed && (
              <img
                src={branding.logo_url}
                alt={branding.company_name}
                onError={() => setLogoFailed(true)}
                className="h-16 w-auto mx-auto mb-4"
              />
            )}
            <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400 font-semibold mb-1">
              {branding.portal_eyebrow}
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Sign in to continue</h1>
          </div>

          {step === "credentials" && adProvider && (
            <form onSubmit={submitAdLogin} className="space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Sign in with your <span className="font-medium">{adProvider.name}</span> (Active
                Directory) account.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Username</label>
                <input
                  type="text"
                  value={adUsername}
                  onChange={(e) => setAdUsername(e.target.value)}
                  required
                  autoFocus
                  autoComplete="username"
                  placeholder="jdoe"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Password</label>
                <input
                  type="password"
                  value={adPassword}
                  onChange={(e) => setAdPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Domain <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={adDomain}
                  onChange={(e) => setAdDomain(e.target.value)}
                  placeholder="corp.local"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading || !adUsername || !adPassword}
                className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 shadow-sm"
              >
                {loading ? "Contacting your directory…" : "Sign In"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAdProvider(null);
                  setError("");
                }}
                className="w-full text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                ← Back to other sign-in options
              </button>
            </form>
          )}

          {step === "credentials" && !adProvider && (
            <form onSubmit={submitCredentials} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 shadow-sm"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>

              {providers.length > 0 && (
                <div className="pt-1">
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                    </div>
                    <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
                      <span className="bg-white dark:bg-gray-800 px-3 text-gray-400 dark:text-gray-500">or</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {providers.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => startSso(p)}
                        className="w-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                      >
                        Sign in with {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </form>
          )}

          {step === "2fa" && (
            <form onSubmit={submitCode} className="space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Enter the {usingBackup ? "backup" : "6-digit"} code from your authenticator app to
                continue.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {usingBackup ? "Backup code" : "Code"}
                </label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode={usingBackup ? "text" : "numeric"}
                  maxLength={usingBackup ? 14 : 6}
                  placeholder={usingBackup ? "ABCD-1234-WXYZ" : "123456"}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-lg font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading || !code}
                className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 shadow-sm"
              >
                {loading ? "Verifying…" : "Verify"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setUsingBackup(!usingBackup);
                  setCode("");
                  setError("");
                }}
                className="w-full text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                {usingBackup ? "Use a 6-digit code instead" : "Use a backup code instead"}
              </button>
            </form>
          )}

          <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-7 pt-5 border-t border-gray-100 dark:border-gray-700">
            Need help? Contact{" "}
            <a
              href={`mailto:${branding.portal_support_email}`}
              className="text-brand dark:text-brand-light font-medium hover:underline"
            >
              {branding.portal_support_email}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
